import { ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSuspenseQuery } from '@tanstack/react-query'

import { Button, Skeleton, Stack } from '@mui/material'

import PageHeader from '@/components/PageHeader'

import { useQueryPaymentCredits } from '@/api/payment/queries'
import { createChargingPointQueryOptions } from '@/api/charging-points/options'

import { ChargingPointInfoHeader } from '@/modules/Station/ChargingPointInfoHeader'

import { routeParser } from '@/routes/helpers/parser'
import { AppRoutes } from '@/routes/types/routes'

import { ChargingPointScreenProps } from '../types'

interface ChargingPointScreenTemplateProps extends ChargingPointScreenProps {
  Header?: ReactNode
  Body?: ReactNode
  Footer?: ReactNode
}

const ChargingPointScreenTemplate: React.FC<ChargingPointScreenTemplateProps> = ({ Body, Footer, Header }) => {
  const params = useParams()
  const navigate = useNavigate()
  const stationId = params.stationId as string
  const chargingPointId = params.chargingPointId as string
  const { data: chargingPoint } = useSuspenseQuery(
    createChargingPointQueryOptions({
      chargingPointId: Number(chargingPointId),
      stationId: Number(params.stationId),
    })
  )

  const handleOnClickGoBack = () => {
    navigate(routeParser(AppRoutes.STATION_CHARGERS, { stationId }))
  }

  const { data: paymentCredits, isLoading: isLoadingPaymentCredits } = useQueryPaymentCredits({
    enabled: true,
    retry: false,
  })

  return (
    <Stack
      paddingTop="24px"
      paddingX="24px"
      overflow="hidden"
      sx={{ height: 'calc(100vh - 112px - env(safe-area-inset-bottom))' }}
    >
      <Stack
        direction="column"
        spacing={2}
        paddingBottom="24px"
        sx={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <PageHeader
          title={chargingPoint.title ?? chargingPoint?.display_title}
          subtitle={chargingPoint.station?.name}
          backRoute={routeParser(AppRoutes.STATION_CHARGERS, { stationId })}
        />
      </Stack>
      <Stack direction="column" alignItems="center" justifyContent="center" spacing={5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} width="100%">
          {isLoadingPaymentCredits ? (
            <Skeleton variant="text" width="100%" height={32} />
          ) : (
            <ChargingPointInfoHeader
              paymentCredits={paymentCredits}
              chargingPoint={chargingPoint}
              isLoading={isLoadingPaymentCredits}
            />
          )}
          {Header}
        </Stack>
      </Stack>
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        flexGrow={1}
        width="100%"
        sx={{ minHeight: 0, overflowY: 'auto' }}
      >
        {Body}
      </Stack>
      <Stack direction="column" alignItems="center" width="100%" spacing={1}>
        {Footer}
        <Button
          color="primary"
          variant="outlined"
          onClick={handleOnClickGoBack}
          sx={{ width: '100%', borderRadius: 4 }}
          disableElevation
        >
          Voltar
        </Button>
      </Stack>
    </Stack>
  )
}

export default ChargingPointScreenTemplate
